import React, { useContext, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ClickAwayListener, MenuList, MenuItem, Popper, Link as MUILink } from '@material-ui/core';
import { IAuthority } from '../../../hooks/useQueryCustomHooks/useGetUserInfoAndAuthorities';
import AuthTopMenu from '../AuthTopMenu';
import { useGetQueryCacheData } from '../../../hooks/useQueryCustomHooks/useGetQueryCacheData';
import apiToUrlMap from '../../../ApiMapping';
import { publicApiMapping } from '../../../hooks/useQueryCustomHooks/usePublicQueryStates';
import { I18nContext } from '../../../I18n';
import { accountMenuItems, MenuItemConfig } from './AccountPopoverMenuUtils';

interface AccountPopoverMenuProps {
  openLogoutDialog: (e: any) => void;
  userData: any;
}

const AccountPopoverMenu: React.FC<AccountPopoverMenuProps> = ({ openLogoutDialog, userData }) => {
  const configState = useGetQueryCacheData<any>(publicApiMapping.config);
  const authorities = useGetQueryCacheData<IAuthority>(apiToUrlMap.authorities)?.authorityObj;
  const I18n = useContext(I18nContext);

  const [popoverMenu, setPopoverMenu] = useState<HTMLElement>();

  const openMenu = (e: React.MouseEvent<HTMLElement>) => setPopoverMenu(e.currentTarget);
  const closeMenu = () => setPopoverMenu(undefined);
  // Filter and sort menu items based on authorities
  const visibleMenuItems = useMemo(() => {
    if (!authorities) return [];
    const configMenuItems = JSON.parse(configState?.accountMenuItems || '[]') as MenuItemConfig[];
    return [...accountMenuItems, ...configMenuItems]
      .filter((item) => !item.authority || (authorities && authorities[item.authority]))
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }, [authorities, configState]);

  const renderMenuItem = (item: MenuItemConfig) => {
    const label = I18n[item.i18nKey] || item.defaultLabel;
    switch (item.action) {
      case 'logout':
        return (
          <MenuItem
            key={item.id}
            data-id={item.id}
            onClick={(e) => {
              closeMenu();
              openLogoutDialog(e);
            }}
            {...(item.props || {})}
          >
            <span className="link">{label}</span>
          </MenuItem>
        );
      case 'externalLink':
        return (
          <MenuItem
            key={item.id}
            data-id={item.id}
            component={MUILink}
            href={item.path || '#'}
            target="_blank"
            rel="noopener noreferrer"
            onClick={closeMenu}
            {...(item.props || {})}
          >
            {label}
          </MenuItem>
        );
      default:
        return (
          <MenuItem
            key={item.id}
            data-id={item.id}
            component={Link}
            to={item.path || '#'}
            onClick={closeMenu}
            {...(item.props || {})}
          >
            {label}
          </MenuItem>
        );
    }
  };

  const menuItems = useMemo(() => {
    return <>{visibleMenuItems.map(renderMenuItem)}</>;
  }, [visibleMenuItems, I18n]);

  return (
    <>
      <AuthTopMenu configState={configState} userData={userData} openMenu={openMenu} />

      {popoverMenu && (
        <Popper
          open={!!popoverMenu}
          placement="bottom-start"
          className="px-popper-menu px-accounts-menu"
          disablePortal={true}
          transition
          keepMounted={false}
          anchorEl={popoverMenu}
        >
          <ClickAwayListener onClickAway={closeMenu}>
            <MenuList>{menuItems}</MenuList>
          </ClickAwayListener>
        </Popper>
      )}
    </>
  );
};

export default AccountPopoverMenu;
